import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import UndoIcon from "@mui/icons-material/Undo";
import { formatNumber } from "../../utils";
import GridCellInput from "../common/EditableGrid/GridCellInput";
import { GridPosition } from "../common/EditableGrid/gridNavigation";
import { ADJUSTED_SX, CELL_SX, ESTIMATE_TEXT_SX, GROUP_SEPARATOR_SX } from "./closeTableStyles";
import { EMPTY_TEXT } from "./constants";
import { CellError, CloseCellTarget, EstimateSide } from "./monthlyExpenseDraft";
import { EstimateFigures } from "./monthlyExpenseMath";
import { formatSignedAmount } from "./monthlyExpenseLayout";

const ESTIMATE_INPUT_WIDTH = 96;
const REVERT_ICON_SIZE = 16;

interface EstimateCellProps {
  branchCode: string;
  side: EstimateSide;
  figures: EstimateFigures;
  error: CellError | undefined;
  position: GridPosition;
  isEditable: boolean;
  /** e.g. "KB · Sales"; names the input and the revert button. */
  label: string;
  /** e.g. "ประมาณการ ต.ค. 2026"; the hover of the input. */
  estimateTitle: string;
  onCommit: (target: CloseCellTarget, text: string) => void;
  onRevert: (branchCode: string, side: EstimateSide) => void;
}

/**
 * Sales or Manager per route per day of one branch. Adjusted, it shows the system value small and
 * grey, the adjusted value bold, and the gap (`ระบบ 1,564.61 / +0.39` beside `1,565.00`), with
 * คืนค่าระบบ; head office types over it directly.
 */
export default function EstimateCell({
  branchCode,
  side,
  figures,
  error,
  position,
  isEditable,
  label,
  estimateTitle,
  onCommit,
  onRevert,
}: Readonly<EstimateCellProps>) {
  const handleRevert = () => onRevert(branchCode, side);
  const effectiveText = figures.effective === null ? EMPTY_TEXT : formatNumber(figures.effective);
  const differenceColor = figures.difference >= 0 ? "success.dark" : "error.main";

  return (
    <TableCell
      sx={[CELL_SX, figures.isAdjusted ? ADJUSTED_SX : {}, side === "SALES" ? GROUP_SEPARATOR_SX : {}]}
    >
      <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.75}>
        {figures.isAdjusted && (
          <Typography component="span" variant="caption" color="text.secondary" textAlign="right" lineHeight={1.25}>
            ระบบ {figures.computed === null ? EMPTY_TEXT : formatNumber(figures.computed)}
            <br />
            <Typography component="b" variant="caption" fontWeight={700} color={differenceColor}>
              {formatSignedAmount(figures.difference)}
            </Typography>
          </Typography>
        )}
        {isEditable ? (
          <GridCellInput
            position={position}
            target={{ kind: "ESTIMATE", branchCode, side }}
            text={error ? error.text : effectiveText}
            onCommit={onCommit}
            ariaLabel={label}
            hasError={Boolean(error)}
            tooltip={error ? error.message : `พิมพ์ทับได้ · ยอดที่ใช้เป็น${estimateTitle}`}
            inputSx={{ ...ESTIMATE_TEXT_SX, width: ESTIMATE_INPUT_WIDTH, minWidth: ESTIMATE_INPUT_WIDTH }}
          />
        ) : (
          <Typography component="span" variant="body2" sx={ESTIMATE_TEXT_SX}>
            {effectiveText}
          </Typography>
        )}
        {isEditable && (
          <Tooltip title="คืนค่าระบบ">
            <IconButton
              size="small"
              aria-label={`คืนค่าระบบ ${label}`}
              onClick={handleRevert}
              sx={{ visibility: figures.isAdjusted ? "visible" : "hidden" }}
            >
              <UndoIcon sx={{ fontSize: REVERT_ICON_SIZE }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </TableCell>
  );
}
